import { type FetchOptions } from 'ofetch';
import { useLoadingStore } from './useLoadingStore';
import { shouldSkipLoading } from '~/config/api-loading-config';
import { getCsrfToken } from '~/composables/useCsrfToken';
import {
  getFetchNetworkErrorMessage,
  handleSessionExpiredFromHeaders,
  isLoginPath,
} from '~/composables/useSessionAuth';
import { notifyApiError, notifyApiSuccess } from '~/composables/useApiToast';
import { markSessionActivity } from '~/composables/useSessionTimeout';
import { appT } from '~/composables/useAppI18n';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// HTML 특수문자 unescape 맵
const HTML_ESCAPE_MAP: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

// XSS 위험 패턴 정규식 (컴파일된 상태로 재사용하여 성능 최적화)
const XSS_PATTERNS: RegExp[] = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, // <script>...</script>
  /javascript\s*:/gi, // javascript:
  /on\w+\s*=/gi, // onclick=, onerror=, onload= 등
  /<iframe\b[^>]*>/gi, // <iframe>
  /<embed\b[^>]*>/gi, // <embed>
  /<object\b[^>]*>/gi, // <object>
  /expression\s*\(/gi, // CSS expression()
  /vbscript\s*:/gi, // vbscript:
  /data\s*:\s*text\/html/gi, // data:text/html
];

/**
 * XSS 위험 문자열을 제거하는 함수
 * @param str 검사할 문자열
 * @returns XSS 패턴이 제거된 안전한 문자열
 */
const sanitizeXSS = (str: string): string => {
  if (!str || typeof str !== 'string') return str;

  let result = str;
  for (const pattern of XSS_PATTERNS) {
    result = result.replace(pattern, '');
  }
  return result;
};

/**
 * HTML 특수문자를 unescape 처리하는 함수
 */
const unescapeHTML = (value: unknown): unknown => {
  if (value == null) return value;

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      // 객체인 경우 재귀 처리, 원시 타입인 경우 문자열 변환 후 처리
      if (typeof value[i] === 'object' && value[i] !== null) {
        value[i] = unescapeHTML(value[i]);
      } else if (value[i] != null) {
        value[i] = unescapeString(String(value[i]));
      }
    }
    return value;
  }

  if (typeof value === 'object') {
    const result = { ...value } as Record<string, unknown>;
    for (const [key, val] of Object.entries(result)) {
      result[key] = unescapeHTML(val);
    }
    return result;
  }

  if (typeof value === 'string') {
    return unescapeString(value);
  }

  return value;
};

/**
 * 문자열의 HTML 특수문자를 unescape 처리하고 XSS 방어 적용
 */
const unescapeString = (str: string): string => {
  if (!str) return str;

  let result = str;

  // &#8203; (Zero Width Space) 제거
  if (result.endsWith('&#8203;')) {
    const lastIndex = result.lastIndexOf('&#8203;');
    result = result.substring(0, lastIndex) + result.substring(lastIndex + 7);
    return result;
  }

  // HTML 특수문자 unescape
  for (const [escaped, unescaped] of Object.entries(HTML_ESCAPE_MAP)) {
    result = result.replaceAll(escaped, unescaped);
  }

  // XSS 위험 패턴 제거
  //   result = sanitizeXSS(result);

  return result;
};

// @ts-ignore
interface ApiOptions<T> extends Omit<FetchOptions<T>, 'body' | 'query'> {
  method?: Method;
  data?: Record<string, any>; // JSON 요청 시 사용
  body?: BodyInit | null; // FormData, string, Blob, etc. 가능
  query?: Record<string, any>;
  skipLoading?: boolean;
  skipUnescape?: boolean; // HTML unescape 처리를 건너뛸지 여부
  skipSessionExpiredCheck?: boolean; // 미인증(X-Authentication-Status: false)이어도 응답 본문 유지 (공개 API)
  /**
   * 실패해도 에러 토스트를 띄우지 않는다.
   *
   * 토스트는 인터셉터에서 띄우므로 호출부의 try/catch 로는 막을 수 없다.
   * 화면 본문이 아니라 프로필 이미지처럼 없어도 그만인 보조 정보를 가져오는 호출에만 쓴다.
   * (조직도 검색 시 행이 대량으로 다시 그려지면 그만큼 요청이 몰려 일부가 실패하는데,
   *  그때마다 "통신 오류" 토스트가 떠서 정상 동작을 오류처럼 보이게 했다)
   */
  skipErrorToast?: boolean;
}

/** sgate-api(/v1/api/*) 요청 여부 — 모바일 Authorization 헤더 부착 대상 */
const isApiServerRequest = (requestUrl: string, apiServerUrl: string): boolean => {
  if (requestUrl.startsWith(apiServerUrl)) return true;
  if (requestUrl.startsWith('/v1/api/')) return true;
  if (apiServerUrl.startsWith('http')) {
    try {
      const { pathname } = new URL(apiServerUrl);
      return pathname !== '/' && requestUrl.startsWith(pathname);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Nuxt 3용 공통 API 요청 컴포저블
 *
 * @param url 요청할 API 엔드포인트
 * @param options ofetch의 FetchOptions
 */
export const useApi = <T = any>(url: string, options: ApiOptions<T> = {}) => {
  const config = useRuntimeConfig();
  const loadingCount = useState('loadingCount', () => 0);
  const isLoading = useLoadingStore();

  // URL이 로딩 생략 대상인지 확인하여 자동 설정
  if (options.skipLoading === undefined && shouldSkipLoading(url)) {
    options.skipLoading = true;
  }

  let processedUrl = url;

  // 환경별 URL 처리
  let baseURL = config.public.apiBase as string;
  // 절대 URL인 경우 baseURL 사용 안 함
  if (url.startsWith('http://') || url.startsWith('https://')) {
    baseURL = '';
  } else {
    // 빌드환경에서는 /api 제거
    processedUrl = config.public.isDev
      ? url // 개발환경: 그대로 사용 (프록시가 처리)
      : url.replace(/^\/api\//, '/'); // 빌드환경: /api/ 제거
  }

  const defaults: FetchOptions = {
    baseURL,
    credentials: 'include', // 🔥 JSESSIONID 쿠키를 보내야 세션 인식됨
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-Requested-From': 'nuxt',
    },
    // 요청 전처리
    onRequest({ options }) {
      if (process.client) {
        const headers = (options.headers ?? {}) as Record<string, string>;
        headers['X-Requested-With'] = headers['X-Requested-With'] || 'XMLHttpRequest';
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          const csrfHeaderName = document.querySelector('meta[name="_csrf_header"]')?.getAttribute('content') || 'X-XSRF-TOKEN';
          headers[csrfHeaderName] = csrfToken;
        }
        options.headers = headers;
      }

      // 모바일 WebView에서 전달받은 Authorization 토큰을 헤더에 추가
      // sgate-api(/v1/api/*) 요청 시 적용 (개발 프록시 상대경로 포함)
      if (process.client && isApiServerRequest(url, config.public.apiServerUrl as string)) {
        const authorization = localStorage.getItem('Authorization');
        if (authorization) {
          options.headers = {
            ...options.headers,
            Authorization: authorization,
          };
        }
      }

      const opts = options as any;
      if (opts.skipLoading !== true) {
        loadingCount.value++;
        isLoading.value = true;
      }
    },

    // 응답 후처리
    onResponse({ response, options }) {
      const opts = options as any;
      if (opts.skipLoading !== true && loadingCount.value > 0) {
        loadingCount.value--;
        if (loadingCount.value === 0) {
          isLoading.value = false;
        }
      }

      // 세션 만료 체크 (X-Authentication-Status: false)
      if (handleSessionExpiredFromHeaders(response.headers) && opts.skipSessionExpiredCheck !== true) {
        response._data = null;
        return;
      }

      // 서버(DB) 왕복 성공 → 세션 유휴 타이머 리셋 (AS-IS layout.jsp 의 initSessionTime 대응)
      markSessionActivity();

      // HTML 특수문자 unescape 처리
      if (response._data && opts.skipUnescape !== true) {
        response._data = unescapeHTML(response._data);
      }

      // 레거시 코드의 'delete' 타입 응답 처리 예시
      if (response._data?.responseType === 'delete') {
        notifyApiSuccess(appT('common.api.deleteSuccess'));
      }
    },

    // 에러 발생 시 후처리
    onResponseError({ response, options }) {
      const opts = options as any;
      if (opts.skipLoading !== true && loadingCount.value > 0) {
        loadingCount.value--;
        if (loadingCount.value === 0) {
          isLoading.value = false;
        }
      }

      // 세션 만료 체크 (X-Authentication-Status: false)
      if (handleSessionExpiredFromHeaders(response?.headers)) {
        return;
      }

      // 서버 왕복(에러 응답이라도 서버까지 다녀옴) → 세션 유휴 타이머 리셋
      markSessionActivity();

      const errorData = response?._data;
      let errorMessage = appT('common.api.processFailed');

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.msg) {
        errorMessage = errorData.msg;
      }

      if (opts.skipErrorToast === true) {
        return;
      }

      if (errorData?.errorType === 'secure') {
        notifyApiError(appT('common.api.securityError'), errorMessage);
        return;
      }

      notifyApiError(appT('common.api.errorOccurred'), errorMessage);
    },

    // 요청 자체가 실패한 경우 (네트워크 에러, 타임아웃, CORS 등) — 세션 만료와 구분
    onRequestError({ error, options }) {
      const opts = options as any;
      if (opts.skipLoading !== true && loadingCount.value > 0) {
        loadingCount.value--;
        if (loadingCount.value === 0) {
          isLoading.value = false;
        }
      }
      if (isLoginPath() || opts.skipErrorToast === true) {
        return;
      }
      notifyApiError(appT('common.api.networkError'), getFetchNetworkErrorMessage(error));
    },
  };

  const mergedOptions = {
    ...defaults,
    ...options,
    headers: {
      ...defaults.headers,
      ...options.headers,
    },
    body: options.body ?? options.data, // ← 이 라인 반드시 있어야 함
    query: options.query,
  };

  if (options.body instanceof FormData) {
    // FormData일 때는 Content-Type을 제거 (브라우저가 boundary 포함하여 자동 설정)
    delete (mergedOptions.headers as Record<string, string>)['Content-Type'];
  }

  // $fetch는 ofetch의 래퍼이며, Nuxt에서 사용이 권장됩니다.
  // @ts-ignore
  return $fetch<T>(processedUrl, mergedOptions);
};
